/**
 * For a composer: as staff write, find the `input` variables the message needs
 * (validate → required_inputs) and hold the values they enter. Pass
 * `variableInputs` as the send body's `variable_inputs`.
 */

import { useEffect, useMemo, useState } from 'react';
import { cleanInputs, templateVariablesApi, type CheckSurface, type RequiredInput, type ValidateResponse } from './api';
import { missingRequiredInputs } from './VariableInputFields';

const HAS_VARIABLE = /\{[^{}]+\}/;

export function useSendInputs({
  subject,
  body,
  appliesTo = 'messages',
  debounceMs = 500,
}: {
  subject?: string | null;
  body: string;
  appliesTo?: CheckSurface;
  debounceMs?: number;
}) {
  const [inputs, setInputs] = useState<RequiredInput[]>([]);
  const [check, setCheck] = useState<ValidateResponse | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});

  useEffect(() => {
    const text = `${subject ?? ''}\n${body ?? ''}`;
    if (!HAS_VARIABLE.test(text)) {
      setInputs([]);
      setCheck(null);
      return;
    }
    let cancelled = false;
    const h = window.setTimeout(() => {
      templateVariablesApi
        .validate({ subject: subject ?? null, body: body ?? '', applies_to: appliesTo })
        .then((res) => {
          if (cancelled) return;
          setInputs(res.required_inputs ?? []);
          setCheck(res);
        })
        .catch(() => {
          if (!cancelled) setCheck(null);
        });
    }, debounceMs);
    return () => {
      cancelled = true;
      window.clearTimeout(h);
    };
  }, [subject, body, appliesTo, debounceMs]);

  const missing = useMemo(() => missingRequiredInputs(inputs, values), [inputs, values]);
  const variableInputs = useMemo(() => {
    const keys = new Set(inputs.map((i) => i.key));
    return cleanInputs(Object.fromEntries(Object.entries(values).filter(([k]) => keys.has(k))));
  }, [inputs, values]);

  const reset = () => setValues({});

  return { inputs, values, setValues, variableInputs, missing, check, reset };
}
